import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  CircularProgress,
  Alert,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Chip,
  ToggleButtonGroup,
  ToggleButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
} from '@mui/material';
import {
  People as UsersIcon,
  School as CoursesIcon,
  CloudUpload as ImportIcon,
  TrendingUp as StatsIcon,
  Settings as SettingsIcon,
  Security as PermissionsIcon,
  Star as StarIcon,
  NewReleases as ReleaseNotesIcon,
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar,
} from 'recharts';
import api from '../services/api';

interface AdminStats {
  users: { total: number };
  courses: { total: number };
  enrollments: { total: number; completed: number; completionRate: string };
  quizzes: { totalAttempts: number; passed: number; passRate: string };
  certificates: { total: number };
  points: { total: number };
  recentEnrollments: Array<{
    id: string;
    createdAt: string;
    user: { displayName: string };
    course: { title: string; slug: string };
  }>;
}

interface ChartData {
  activityChart: Array<{ date: string; enrollments: number; completions: number }>;
  topUsers: Array<{ id: string; name: string; points: number; completedCourses: number }>;
  topCourses: Array<{ id: string; title: string; slug: string; enrollments: number; isPublished: boolean }>;
}

const adminCards = [
  {
    path: '/admin/users',
    labelKey: 'admin.users',
    icon: <UsersIcon sx={{ fontSize: 48 }} />,
    color: '#1976d2',
    description: 'Benutzer verwalten, Rollen zuweisen'
  },
  {
    path: '/admin/modules',
    labelKey: 'admin.modules',
    icon: <PermissionsIcon sx={{ fontSize: 48 }} />,
    color: '#d32f2f',
    description: 'Modul-Berechtigungen verwalten'
  },
  {
    path: '/admin/courses',
    labelKey: 'admin.courses',
    icon: <CoursesIcon sx={{ fontSize: 48 }} />,
    color: '#2e7d32',
    description: 'Kurse erstellen und bearbeiten'
  },
  {
    path: '/admin/import',
    labelKey: 'admin.imports',
    icon: <ImportIcon sx={{ fontSize: 48 }} />,
    color: '#ed6c02',
    description: 'SCORM, PDF, Weblinks importieren'
  },
  {
    path: '/admin/release-notes',
    labelKey: 'admin.releaseNotes',
    icon: <ReleaseNotesIcon sx={{ fontSize: 48 }} />,
    color: '#00796b',
    description: 'Release Notes verwalten und veröffentlichen'
  },
];

export default function AdminDashboardPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [chartDays, setChartDays] = useState<number>(30);

  const { data: stats, isLoading, error } = useQuery<AdminStats>({
    queryKey: ['admin', 'stats'],
    queryFn: async () => {
      const response = await api.get('/admin/stats');
      return response.data;
    },
  });

  const { data: chartData } = useQuery<ChartData>({
    queryKey: ['admin', 'charts', chartDays],
    queryFn: async () => {
      const response = await api.get(`/admin/stats/charts?days=${chartDays}`);
      return response.data;
    },
  });

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {t('errors.generic')}
      </Alert>
    );
  }

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 3 }}>
        {t('admin.dashboard')}
      </Typography>

      {/* Stats Overview */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={6} md={2}>
          <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'primary.main', color: 'white' }}>
            <Typography variant="h4">{stats?.users.total || 0}</Typography>
            <Typography variant="body2">Benutzer</Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} md={2}>
          <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'success.main', color: 'white' }}>
            <Typography variant="h4">{stats?.courses.total || 0}</Typography>
            <Typography variant="body2">Kurse</Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} md={2}>
          <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'warning.main', color: 'white' }}>
            <Typography variant="h4">{stats?.enrollments.total || 0}</Typography>
            <Typography variant="body2">Einschreibungen</Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} md={2}>
          <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'info.main', color: 'white' }}>
            <Typography variant="h4">{stats?.enrollments.completionRate || 0}%</Typography>
            <Typography variant="body2">Abschlussrate</Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} md={2}>
          <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'secondary.main', color: 'white' }}>
            <Typography variant="h4">{stats?.certificates.total || 0}</Typography>
            <Typography variant="body2">Zertifikate</Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} md={2}>
          <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'error.main', color: 'white' }}>
            <Typography variant="h4">{((stats?.points.total || 0) / 1000).toFixed(1)}k</Typography>
            <Typography variant="body2">Gesamtpunkte</Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Activity Chart */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">Aktivität</Typography>
            <ToggleButtonGroup
              value={chartDays}
              exclusive
              onChange={(_, value) => value && setChartDays(value)}
              size="small"
            >
              <ToggleButton value={7}>7 Tage</ToggleButton>
              <ToggleButton value={30}>30 Tage</ToggleButton>
              <ToggleButton value={90}>90 Tage</ToggleButton>
            </ToggleButtonGroup>
          </Box>
          {chartData?.activityChart && chartData.activityChart.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={chartData.activityChart}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="date"
                  tickFormatter={(value) => {
                    const date = new Date(value);
                    return `${date.getDate()}.${date.getMonth() + 1}`;
                  }}
                />
                <YAxis />
                <Tooltip
                  labelFormatter={(value) => new Date(value).toLocaleDateString('de-DE')}
                />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="enrollments"
                  name="Einschreibungen"
                  stroke="#1976d2"
                  strokeWidth={2}
                />
                <Line
                  type="monotone"
                  dataKey="completions"
                  name="Abschlüsse"
                  stroke="#2e7d32"
                  strokeWidth={2}
                />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <Box textAlign="center" py={4} color="text.secondary">
              Keine Aktivitätsdaten verfügbar
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Top Users and Top Courses */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Top Users */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>
                <StarIcon sx={{ verticalAlign: 'middle', mr: 1, color: 'warning.main' }} />
                Top 10 Benutzer
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>#</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell align="right">Punkte</TableCell>
                      <TableCell align="right">Kurse</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {chartData?.topUsers?.map((user, index) => (
                      <TableRow key={user.id}>
                        <TableCell>{index + 1}</TableCell>
                        <TableCell>{user.name}</TableCell>
                        <TableCell align="right">{user.points}</TableCell>
                        <TableCell align="right">{user.completedCourses}</TableCell>
                      </TableRow>
                    ))}
                    {(!chartData?.topUsers || chartData.topUsers.length === 0) && (
                      <TableRow>
                        <TableCell colSpan={4} align="center">
                          Keine Daten
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Top Courses */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>
                <CoursesIcon sx={{ verticalAlign: 'middle', mr: 1, color: 'success.main' }} />
                Top 10 Kurse
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>#</TableCell>
                      <TableCell>Kurs</TableCell>
                      <TableCell align="right">Einschreibungen</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {chartData?.topCourses?.map((course, index) => (
                      <TableRow
                        key={course.id}
                        sx={{ cursor: 'pointer' }}
                        hover
                        onClick={() => navigate(`/courses/${course.slug}`)}
                      >
                        <TableCell>{index + 1}</TableCell>
                        <TableCell>{course.title}</TableCell>
                        <TableCell align="right">{course.enrollments}</TableCell>
                      </TableRow>
                    ))}
                    {(!chartData?.topCourses || chartData.topCourses.length === 0) && (
                      <TableRow>
                        <TableCell colSpan={3} align="center">
                          Keine Daten
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Admin Cards */}
      <Typography variant="h6" sx={{ mb: 2 }}>Verwaltung</Typography>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {adminCards.map((card) => (
          <Grid item xs={12} sm={6} md={4} key={card.path}>
            <Card
              sx={{
                height: '100%',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 4,
                }
              }}
            >
              <CardActionArea
                onClick={() => navigate(card.path)}
                sx={{ height: '100%', p: 2 }}
              >
                <CardContent sx={{ textAlign: 'center' }}>
                  <Box sx={{ color: card.color, mb: 2 }}>
                    {card.icon}
                  </Box>
                  <Typography variant="h6" gutterBottom>
                    {t(card.labelKey)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {card.description}
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Recent Enrollments */}
      <Typography variant="h6" sx={{ mb: 2 }}>Letzte Einschreibungen</Typography>
      <Paper>
        <List>
          {stats?.recentEnrollments?.slice(0, 5).map((enrollment) => (
            <ListItem key={enrollment.id} divider>
              <ListItemAvatar>
                <Avatar sx={{ bgcolor: 'primary.main' }}>
                  {enrollment.user.displayName?.charAt(0) || 'U'}
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={enrollment.user.displayName}
                secondary={`Eingeschrieben in: ${enrollment.course.title}`}
              />
              <Chip
                label={new Date(enrollment.createdAt).toLocaleDateString('de-DE')}
                size="small"
                variant="outlined"
              />
            </ListItem>
          ))}
          {(!stats?.recentEnrollments || stats.recentEnrollments.length === 0) && (
            <ListItem>
              <ListItemText primary="Keine Einschreibungen vorhanden" />
            </ListItem>
          )}
        </List>
      </Paper>
    </Box>
  );
}
